import React, { useEffect, useState, useCallback } from 'react';
import { FiPlus } from 'react-icons/fi';
import { frontDeskApi } from '../../api/frontDesk';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import { PhoneLink } from '../../components/PhoneLink';
import { PasswordFieldToggle } from '../../components/PasswordFieldToggle';
import { FdPerPageField, FdPaginationNav } from '../../components/FdDataTableControls';

/** Matches API + fallback when comparing property.owner_id to assignment user. */
function rowIsListedPropertyOwner(row) {
  if (!row) return false;
  if (row.is_listed_property_owner) return true;
  const uid = row.user_id ?? row.user?.id;
  if (row.property_owner_id != null && uid != null && Number(row.property_owner_id) === Number(uid)) {
    return true;
  }
  return false;
}

function staffStatusLabel(row) {
  if (row?.separated_at) return { key: 'exited', label: 'Exited' };
  if (row && row.is_active === false) return { key: 'inactive', label: 'Inactive' };
  return { key: 'active', label: 'Active' };
}

const STAFF_SEPARATION_TYPES = [
  { value: 'voluntary_resignation', label: 'Voluntary resignation' },
  { value: 'dismissal', label: 'Dismissal' },
  { value: 'end_of_contract', label: 'End of contract' },
  { value: 'mutual_agreement', label: 'Mutual agreement' },
  { value: 'redundancy', label: 'Redundancy / role eliminated' },
  { value: 'other', label: 'Other' },
];

function EmptyTableRow({ colSpan, message }) {
  return (
    <tr>
      <td colSpan={colSpan} className="fd-table-empty">
        <span>{message}</span>
      </td>
    </tr>
  );
}

export function FrontDeskUsersPanel({
  propertyId,
  teamRows,
  teamLoading,
  staffMeta,
  staffPage,
  setStaffPage,
  staffPerPage,
  setStaffPerPage,
  staffQDraft,
  setStaffQDraft,
  staffKycFilter,
  setStaffKycFilter,
  staffIncludeInactive,
  setStaffIncludeInactive,
  loadTeamList,
  canStaffCreateFd,
  canStaffUserCreateFd = false,
  canStaffRecordCreateFd = false,
  canStaffUpdateFd,
  canStaffDeleteFd,
  canPropertyOwnerAssignFd = false,
  showKycReviewFd,
  setKycEditAssignment,
  setError,
  setSuccessMessage,
  apartmentOptions,
}) {
  const [jobTitles, setJobTitles] = useState([]);
  /** @type {{ id: number, name: string, slug: string, description?: string|null, abilities: string[] }[]} */
  const [permissionRoles, setPermissionRoles] = useState([]);
  const [createPermissionRoleId, setCreatePermissionRoleId] = useState('');
  const [editPermissionRoleId, setEditPermissionRoleId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  /** @type {'login'|'offline'} */
  const [createMode, setCreateMode] = useState('login');
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    staff_job_title_id: '',
    display_name: '',
    contact_email: '',
    contact_phone: '',
    hired_at: '',
  });
  const [createReceivesOwner, setCreateReceivesOwner] = useState(false);
  const [createListedOwner, setCreateListedOwner] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editReceivesOwner, setEditReceivesOwner] = useState(false);
  const [editListedOwner, setEditListedOwner] = useState(false);
  const [editApartmentId, setEditApartmentId] = useState('');
  const [editJobTitleId, setEditJobTitleId] = useState('');
  const [editJobTitleText, setEditJobTitleText] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editKycStatus, setEditKycStatus] = useState('');
  const [editKycNotes, setEditKycNotes] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editSeparatedAt, setEditSeparatedAt] = useState('');
  const [editSeparationType, setEditSeparationType] = useState('');
  const [editSeparationNotes, setEditSeparationNotes] = useState('');
  const [editHiredAt, setEditHiredAt] = useState('');
  const [busy, setBusy] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const roleParams = propertyId ? { for_property: propertyId } : {};
      const [j, r] = await Promise.all([
        frontDeskApi.staffJobTitles(),
        frontDeskApi.staffPermissionRoles(roleParams),
      ]);
      setJobTitles(Array.isArray(j.data) ? j.data : []);
      setPermissionRoles(Array.isArray(r.data) ? r.data : []);
    } catch {
      setJobTitles([]);
      setPermissionRoles([]);
    }
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) {
      loadMeta();
    }
  }, [propertyId, loadMeta]);

  const openEdit = (row) => {
    setEditRow(row);
    setEditPermissionRoleId(row.permission_role?.id != null ? String(row.permission_role.id) : '');
    setEditApartmentId(row.apartment_id != null ? String(row.apartment_id) : '');
    setEditJobTitleId(row.staff_job_title_id != null ? String(row.staff_job_title_id) : '');
    setEditJobTitleText(row.job_title && !row.staff_job_title_id ? row.job_title : '');
    setEditActive(row.is_active !== false);
    setEditKycStatus(row.kyc_status || 'pending');
    setEditKycNotes(row.kyc_notes || '');
    setEditReceivesOwner(Boolean(row.receives_owner_notifications));
    setEditListedOwner(rowIsListedPropertyOwner(row));
    setEditDisplayName(row.display_name || row.user?.name || '');
    setEditContactEmail(row.contact_email || '');
    setEditContactPhone(row.contact_phone || '');
    setEditSeparatedAt(row.separated_at || '');
    setEditSeparationType(row.separation_type || '');
    setEditSeparationNotes(row.separation_notes || '');
    setEditHiredAt(row.hired_at || '');
  };

  const showActions = canStaffUpdateFd || canStaffDeleteFd || showKycReviewFd;
  const colCount = showActions ? 9 : 8;

  return (
    <>
      <h2 className="front-desk-section-title">Users</h2>
      <p className="front-desk-section-lead">
        Colleagues assigned to this property. The property record owner (properties.owner_id) is managed in admin and does not
        appear here. Create Front Desk accounts, pick a permission template, job title, and hire date. Salary and sensitive ID
        fields follow payroll and KYC permissions. Use Roles &amp; permissions in the sidebar to edit templates themselves.
      </p>
      {!propertyId ? (
        <p className="muted">Select a property above.</p>
      ) : (
        <>
          <div className="fd-bookings-toolbar" style={{ marginBottom: '1rem' }}>
            <label className="fd-bookings-toolbar-field">
              <span className="fd-bookings-toolbar-label">Search</span>
              <input
                type="search"
                value={staffQDraft}
                onChange={(e) => setStaffQDraft(e.target.value)}
                placeholder="Name or email"
              />
            </label>
            <label className="fd-bookings-toolbar-field">
              <span className="fd-bookings-toolbar-label">KYC</span>
              <select value={staffKycFilter} onChange={(e) => setStaffKycFilter(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </label>
            <label className="fd-bookings-toolbar-field fd-bookings-toolbar-field--checkbox">
              <input
                type="checkbox"
                checked={staffIncludeInactive}
                onChange={(e) => setStaffIncludeInactive(e.target.checked)}
              />
              <span>Show inactive</span>
            </label>
            <FdPerPageField
              id="fd-staff-per-page"
              value={staffPerPage}
              onChange={setStaffPerPage}
              maxAllowed={250}
            />
            {canStaffCreateFd ? (
              <div className="fd-bookings-toolbar-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {canStaffUserCreateFd ? (
                  <button
                    type="button"
                    className="btn-primary fd-btn-with-icon"
                    onClick={() => {
                      setCreateMode('login');
                      setCreateForm({
                        name: '',
                        email: '',
                        phone_number: '',
                        password: '',
                        staff_job_title_id: '',
                        display_name: '',
                        contact_email: '',
                        contact_phone: '',
                        hired_at: '',
                      });
                      setCreatePermissionRoleId('');
                      setCreateReceivesOwner(false);
                      setCreateListedOwner(false);
                      setCreateOpen(true);
                    }}
                  >
                    <FiPlus aria-hidden />
                    New Front Desk user
                  </button>
                ) : null}
                {canStaffRecordCreateFd ? (
                  <button
                    type="button"
                    className="btn-primary fd-btn-with-icon"
                    onClick={() => {
                      setCreateMode('offline');
                      setCreateForm({
                        name: '',
                        email: '',
                        phone_number: '',
                        password: '',
                        staff_job_title_id: '',
                        display_name: '',
                        contact_email: '',
                        contact_phone: '',
                        hired_at: '',
                      });
                      setCreatePermissionRoleId('');
                      setCreateReceivesOwner(false);
                      setCreateListedOwner(false);
                      setCreateOpen(true);
                    }}
                  >
                    <FiPlus aria-hidden />
                    New staff (no login)
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {teamLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <div className="fd-table-wrap">
              <table className="fd-table fd-table--wide">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Job title</th>
                    <th>Permission template</th>
                    <th>Status</th>
                    <th>KYC</th>
                    <th>Owner / alerts</th>
                    {showActions ? <th className="fd-col-nowrap fd-actions-col-start">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {teamRows.length === 0 ? (
                    <EmptyTableRow colSpan={colCount} message="No staff for this property." />
                  ) : (
                    teamRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {row.user?.avatar_url ? (
                              <img
                                src={row.user.avatar_url}
                                alt=""
                                style={{ width: 36, height: 36, borderRadius: '9999px', objectFit: 'cover' }}
                              />
                            ) : null}
                            <div>
                              <div>{row.user?.name || row.display_name || '—'}</div>
                              <div className="muted" style={{ fontSize: '0.8125rem' }}>
                                {row.user?.email || row.contact_email || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <PhoneLink phone={row.user?.phone_number} />
                        </td>
                        <td>{row.user?.role || '—'}</td>
                        <td>{row.job_title || '—'}</td>
                        <td style={{ fontSize: '0.8125rem', maxWidth: 180 }}>
                          {row.permission_role?.name ? (
                            <span style={{ fontWeight: 600 }}>{row.permission_role.name}</span>
                          ) : (row.abilities || []).length > 0 ? (
                            <span className="muted">Legacy (no template)</span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const st = staffStatusLabel(row);
                            const colors =
                              st.key === 'active'
                                ? { bg: 'rgba(22, 163, 74, 0.14)', fg: '#15803d' }
                                : st.key === 'exited'
                                  ? { bg: 'rgba(127, 29, 29, 0.12)', fg: '#991b1b' }
                                  : { bg: 'rgba(107, 114, 128, 0.18)', fg: '#4b5563' };
                            return (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 6,
                                  background: colors.bg,
                                  color: colors.fg,
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {st.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{row.kyc_status || '—'}</td>
                        <td style={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
                          {row.is_listed_property_owner ? (
                            <span
                              style={{
                                display: 'inline-block',
                                marginRight: 6,
                                marginBottom: 4,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: 'rgba(15, 118, 110, 0.15)',
                                color: 'var(--fd-accent-strong, #0f766e)',
                                fontWeight: 600,
                              }}
                            >
                              Listed owner
                            </span>
                          ) : null}
                          {row.receives_owner_notifications ? (
                            <span
                              style={{
                                display: 'inline-block',
                                marginBottom: 4,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: 'rgba(59, 130, 246, 0.12)',
                                color: '#1d4ed8',
                                fontWeight: 600,
                              }}
                            >
                              Owner emails
                            </span>
                          ) : null}
                          {!row.is_listed_property_owner && !row.receives_owner_notifications ? (
                            <span className="muted">—</span>
                          ) : null}
                        </td>
                        {showActions ? (
                          <td className="fd-actions-col-start">
                            <div className="fd-finance-row-actions">
                              {canStaffUpdateFd ? (
                                <button
                                  type="button"
                                  className="btn-ghost fd-finance-row-action"
                                  onClick={() => openEdit(row)}
                                >
                                  Edit
                                </button>
                              ) : null}
                              {showKycReviewFd ? (
                                <button
                                  type="button"
                                  className="btn-ghost fd-finance-row-action"
                                  onClick={() => setKycEditAssignment(row)}
                                >
                                  Review KYC
                                </button>
                              ) : null}
                              {canStaffDeleteFd ? (
                                <button
                                  type="button"
                                  className="btn-ghost fd-finance-row-action"
                                  onClick={async () => {
                                    const ok = await luxisttConfirm({
                                      title: 'Remove assignment?',
                                      text: `Remove ${row.user?.name || row.display_name || 'this staff member'} from this property?`,
                                      icon: 'warning',
                                      confirmText: 'Remove',
                                    });
                                    if (!ok) return;
                                    setError('');
                                    try {
                                      await frontDeskApi.deleteStaffAssignment(row.id);
                                      setSuccessMessage('Assignment removed.');
                                      await loadTeamList();
                                    } catch (err) {
                                      setError(err.message || 'Could not remove');
                                    }
                                  }}
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {staffMeta ? (
            <FdPaginationNav
              meta={staffMeta}
              page={staffPage}
              setPage={setStaffPage}
              disabled={teamLoading}
            />
          ) : null}
        </>
      )}

      {createOpen ? (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setCreateOpen(false);
          }}
        >
          <div
            className="modal-box modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-staff-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="fd-staff-create-title">
              {createMode === 'login' ? 'New Front Desk user' : 'New staff (no login)'}
            </h3>
            <p className="muted">
              {createMode === 'login'
                ? 'Creates a Front Desk account and assigns them to this property. They receive an email to set their password.'
                : 'Adds someone to the roster and payroll only — no app account or invite email.'}
            </p>
            <div className="fd-finance-form__grid" style={{ marginTop: 12 }}>
              {createMode === 'login' ? (
                <>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Name</span>
                    <input
                      className="fd-finance-input"
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Email</span>
                    <input
                      className="fd-finance-input"
                      type="email"
                      autoComplete="off"
                      value={createForm.email}
                      onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </label>
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Phone</span>
                    <input
                      className="fd-finance-input"
                      type="tel"
                      autoComplete="off"
                      placeholder="+2348012345678"
                      value={createForm.phone_number}
                      onChange={(e) => setCreateForm((f) => ({ ...f, phone_number: e.target.value }))}
                    />
                  </label>
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Password (optional)</span>
                    <PasswordFieldToggle
                      label={null}
                      autoComplete="new-password"
                      placeholder="Leave empty — email link"
                      value={createForm.password}
                      onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                      inputClassName="fd-finance-input"
                      errorClassName="muted"
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Display name</span>
                    <input
                      className="fd-finance-input"
                      value={createForm.display_name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, display_name: e.target.value }))}
                    />
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Contact email (optional)</span>
                    <input
                      className="fd-finance-input"
                      type="email"
                      autoComplete="off"
                      value={createForm.contact_email}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_email: e.target.value }))}
                    />
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Contact phone (optional)</span>
                    <input
                      className="fd-finance-input"
                      type="tel"
                      autoComplete="off"
                      value={createForm.contact_phone}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_phone: e.target.value }))}
                    />
                  </label>
                </>
              )}
              {jobTitles.length > 0 ? (
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Job title</span>
                  <select
                    className="fd-finance-input"
                    value={createForm.staff_job_title_id}
                    onChange={(e) => setCreateForm((f) => ({ ...f, staff_job_title_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {jobTitles.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="fd-finance-field">
                <span className="fd-finance-field__label">Hire date (optional)</span>
                <input
                  className="fd-finance-input"
                  type="date"
                  value={createForm.hired_at}
                  onChange={(e) => setCreateForm((f) => ({ ...f, hired_at: e.target.value }))}
                />
              </label>
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Owner-style email alerts</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={createReceivesOwner}
                    onChange={(e) => setCreateReceivesOwner(e.target.checked)}
                  />
                  Send this person the same booking and finance alerts as the property record owner
                </label>
              </label>
              {createMode === 'login' && canPropertyOwnerAssignFd ? (
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Listed property owner</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={createListedOwner}
                      onChange={(e) => setCreateListedOwner(e.target.checked)}
                    />
                    Set as properties.owner_id and grant Owner role (requires HR: set listed owner permission)
                  </label>
                </label>
              ) : null}
            </div>
            {permissionRoles.length > 0 ? (
              <label className="fd-finance-field fd-finance-field--span2" style={{ marginTop: 16 }}>
                <span className="fd-finance-field__label">Apply permission role template</span>
                <select
                  className="fd-finance-input"
                  value={createPermissionRoleId}
                  onChange={(e) => setCreatePermissionRoleId(e.target.value)}
                >
                  <option value="">None — no front-desk module access</option>
                  {permissionRoles.map((role) => (
                    <option key={role.id} value={String(role.id)}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {(() => {
                  const sel = permissionRoles.find((r) => String(r.id) === createPermissionRoleId);
                  return sel?.description ? (
                    <span className="muted" style={{ display: 'block', marginTop: 6, fontSize: '0.8125rem' }}>
                      {sel.description}
                    </span>
                  ) : null;
                })()}
              </label>
            ) : null}
            <div className="fd-shift-modal-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn-ghost" disabled={busy} onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    let body;
                    if (createMode === 'login') {
                      body = {
                        name: createForm.name.trim(),
                        email: createForm.email.trim(),
                        phone_number: createForm.phone_number.trim() || null,
                        password: createForm.password || undefined,
                        receives_owner_notifications: createReceivesOwner,
                      };
                      if (createForm.staff_job_title_id) {
                        body.staff_job_title_id = Number(createForm.staff_job_title_id);
                      }
                      if (createPermissionRoleId) {
                        body.staff_permission_role_id = Number(createPermissionRoleId);
                      }
                      if (canPropertyOwnerAssignFd) {
                        body.listed_property_owner = createListedOwner;
                      }
                    } else {
                      body = {
                        without_login: true,
                        display_name: createForm.display_name.trim(),
                        contact_email: createForm.contact_email.trim() || null,
                        contact_phone: createForm.contact_phone.trim() || null,
                        receives_owner_notifications: createReceivesOwner,
                      };
                      if (createForm.staff_job_title_id) {
                        body.staff_job_title_id = Number(createForm.staff_job_title_id);
                      }
                      if (createPermissionRoleId) {
                        body.staff_permission_role_id = Number(createPermissionRoleId);
                      }
                    }
                    const hire = (createForm.hired_at || '').trim();
                    if (hire) {
                      body.hired_at = hire;
                    }
                    await frontDeskApi.createStaffAssignment(propertyId, body);
                    setSuccessMessage(createMode === 'login' ? 'User created and assigned.' : 'Staff record created.');
                    setCreateOpen(false);
                    await loadTeamList();
                  } catch (err) {
                    setError(err.message || 'Could not create');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? 'Saving…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editRow ? (
        <div
          className="modal-backdrop fd-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setEditRow(null);
          }}
        >
          <div
            className="modal-box modal-wide"
            style={{ maxHeight: '90vh', overflow: 'auto' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fd-staff-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="fd-staff-edit-title">Edit assignment</h3>
            <p className="muted">
              {editRow.without_login
                ? `On-site staff · ${editDisplayName || '—'}`
                : `${editRow.user?.name || '—'} · ${editRow.user?.email || '—'}`}
            </p>
            <div className="fd-finance-form__grid" style={{ marginTop: 12 }}>
              {editRow.without_login ? (
                <>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Display name</span>
                    <input
                      className="fd-finance-input"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                    />
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Contact email (optional)</span>
                    <input
                      className="fd-finance-input"
                      type="email"
                      value={editContactEmail}
                      onChange={(e) => setEditContactEmail(e.target.value)}
                    />
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Contact phone (optional)</span>
                    <input
                      className="fd-finance-input"
                      type="tel"
                      value={editContactPhone}
                      onChange={(e) => setEditContactPhone(e.target.value)}
                    />
                  </label>
                </>
              ) : null}
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Unit scope</span>
                <select
                  className="fd-finance-input"
                  value={editApartmentId}
                  onChange={(e) => setEditApartmentId(e.target.value)}
                >
                  <option value="">Whole property</option>
                  {apartmentOptions.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
              {jobTitles.length > 0 ? (
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Job title</span>
                  <select
                    className="fd-finance-input"
                    value={editJobTitleId}
                    onChange={(e) => setEditJobTitleId(e.target.value)}
                  >
                    <option value="">—</option>
                    {jobTitles.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Custom title (optional)</span>
                <input
                  className="fd-finance-input"
                  value={editJobTitleText}
                  onChange={(e) => setEditJobTitleText(e.target.value)}
                />
              </label>
              {canStaffUpdateFd ? (
                <label className="fd-finance-field">
                  <span className="fd-finance-field__label">Hire date (optional)</span>
                  <input
                    className="fd-finance-input"
                    type="date"
                    value={editHiredAt}
                    onChange={(e) => setEditHiredAt(e.target.value)}
                  />
                </label>
              ) : null}
              <label className="fd-finance-field fd-finance-field--span2">
                <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                <span style={{ marginLeft: 8 }}>Active assignment</span>
              </label>
              {canStaffUpdateFd ? (
                <>
                  <p className="muted fd-finance-field--span2" style={{ margin: '4px 0 0', fontSize: '0.8125rem' }}>
                    Separation (optional): capture exit date and reason for HR reports and planning.
                  </p>
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Exit / last day</span>
                    <input
                      className="fd-finance-input"
                      type="date"
                      value={editSeparatedAt}
                      onChange={(e) => setEditSeparatedAt(e.target.value)}
                    />
                  </label>
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">Exit type</span>
                    <select
                      className="fd-finance-input"
                      value={editSeparationType}
                      onChange={(e) => setEditSeparationType(e.target.value)}
                    >
                      <option value="">—</option>
                      {STAFF_SEPARATION_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">Exit notes</span>
                    <textarea
                      className="fd-finance-input fd-finance-input--textarea"
                      rows={2}
                      value={editSeparationNotes}
                      onChange={(e) => setEditSeparationNotes(e.target.value)}
                      placeholder="Optional context (performance, relocation, etc.)"
                    />
                  </label>
                </>
              ) : null}
              {showKycReviewFd && !editRow.without_login ? (
                <>
                  <label className="fd-finance-field">
                    <span className="fd-finance-field__label">KYC status</span>
                    <select
                      className="fd-finance-input"
                      value={editKycStatus}
                      onChange={(e) => setEditKycStatus(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="submitted">Submitted</option>
                      <option value="verified">Verified</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                  <label className="fd-finance-field fd-finance-field--span2">
                    <span className="fd-finance-field__label">KYC notes</span>
                    <textarea
                      className="fd-finance-input"
                      rows={2}
                      value={editKycNotes}
                      onChange={(e) => setEditKycNotes(e.target.value)}
                    />
                  </label>
                </>
              ) : null}
              <label className="fd-finance-field fd-finance-field--span2">
                <span className="fd-finance-field__label">Owner-style email alerts</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={editReceivesOwner}
                    onChange={(e) => setEditReceivesOwner(e.target.checked)}
                  />
                  Send this person the same booking and finance alerts as the property record owner
                </label>
              </label>
              {canPropertyOwnerAssignFd && !editRow.without_login ? (
                <label className="fd-finance-field fd-finance-field--span2">
                  <span className="fd-finance-field__label">Listed property owner</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={editListedOwner}
                      onChange={(e) => setEditListedOwner(e.target.checked)}
                    />
                    Property record owner (properties.owner_id). Uncheck only after assigning another owner.
                  </label>
                </label>
              ) : null}
            </div>
            {permissionRoles.length > 0 ? (
              <label className="fd-finance-field fd-finance-field--span2" style={{ marginTop: 16 }}>
                <span className="fd-finance-field__label">Permission role template</span>
                <select
                  className="fd-finance-input"
                  value={editPermissionRoleId}
                  onChange={(e) => setEditPermissionRoleId(e.target.value)}
                >
                  <option value="">None — no template (legacy access kept until you pick one)</option>
                  {permissionRoles.map((role) => (
                    <option key={role.id} value={String(role.id)}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {(() => {
                  const sel = permissionRoles.find((r) => String(r.id) === editPermissionRoleId);
                  return sel?.description ? (
                    <span className="muted" style={{ display: 'block', marginTop: 6, fontSize: '0.8125rem' }}>
                      {sel.description}
                    </span>
                  ) : null;
                })()}
              </label>
            ) : null}
            <div className="fd-shift-modal-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn-ghost" disabled={busy} onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    const hadTemplate = editRow.permission_role?.id != null;
                    const legacyAbilities = Array.isArray(editRow.abilities) ? editRow.abilities : [];
                    const payload = {
                      apartment_id: editApartmentId ? Number(editApartmentId) : null,
                      staff_job_title_id: editJobTitleId ? Number(editJobTitleId) : null,
                      job_title: editJobTitleText.trim() || null,
                      is_active: editActive,
                      receives_owner_notifications: editReceivesOwner,
                      staff_permission_role_id: editPermissionRoleId ? Number(editPermissionRoleId) : null,
                    };
                    if (!editPermissionRoleId && !hadTemplate && legacyAbilities.length > 0) {
                      payload.abilities = legacyAbilities;
                    }
                    if (editRow.without_login) {
                      payload.display_name = editDisplayName.trim();
                      payload.contact_email = editContactEmail.trim() || null;
                      payload.contact_phone = editContactPhone.trim() || null;
                    }
                    if (canPropertyOwnerAssignFd && !editRow.without_login) {
                      payload.listed_property_owner = editListedOwner;
                    }
                    if (showKycReviewFd && !editRow.without_login) {
                      payload.kyc_status = editKycStatus;
                      payload.kyc_notes = editKycNotes || null;
                    }
                    if (canStaffUpdateFd) {
                      payload.separated_at = editSeparatedAt.trim() || null;
                      payload.separation_type = editSeparationType.trim() || null;
                      payload.separation_notes = editSeparationNotes.trim() || null;
                      payload.hired_at = editHiredAt.trim() || null;
                    }
                    await frontDeskApi.updateStaffAssignment(editRow.id, payload);
                    setSuccessMessage('Assignment updated.');
                    setEditRow(null);
                    await loadTeamList();
                  } catch (err) {
                    setError(err.message || 'Could not save');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
